import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Pill, TextField } from '../ui'
import { SessionLockBanner } from './SessionLockBanner.jsx'
import { PrivateNotesCard } from './PrivateNotesCard.jsx'
import { PrintView } from './PrintView.jsx'
import { GoalCanvasZone } from '../goals/GoalCanvasZone.jsx'
import { AgendaSection } from '../agenda/AgendaSection.jsx'
import { TaskList } from './tasks/TaskList.jsx'
import { PrepCard } from '../cards/preparation/PrepCard.jsx'
import { PrepCardTray } from '../cards/preparation/PrepCardTray.jsx'
import { getPrepCard, PREP_CARD_IDS, PREP_CORE_IDS } from '../cards/preparation/prepCardDefinitions.js'
import { SessionCard } from '../cards/inSession/SessionCard.jsx'
import { SessionCardTray } from '../cards/inSession/SessionCardTray.jsx'
import { getSessionCard, SESSION_CARD_IDS, SESSION_CORE_IDS } from '../cards/inSession/sessionCardDefinitions.js'
import { useAutoSave } from '../hooks/useAutoSave.js'
import { useSubscription } from '../api/useSubscription.js'
import { appsyncQuery } from '../api/appsync-client.js'
import { ReadyFlipButton } from './ReadyFlipButton.jsx'
import { ConfirmFlipButton } from './ConfirmFlipButton.jsx'
import { useCardHistory } from '../cards/preparation/interactions/useCardHistory.js'
import { CardHistoryBack, CARD_HISTORY_CONFIGS } from '../cards/preparation/interactions/CardHistoryBack.jsx'

const GET_SESSION = `
  query GetSession($sessionId: ID!) {
    getSession(sessionId: $sessionId) {
      sessionId userId managerId status readyAt completedAt cancelledAt
      sections layout agenda scheduledAt updatedAt taskSnapshot
    }
  }
`

const GET_REPORT_SESSION = `
  query GetReportSession($reportId: ID!, $sessionId: ID!) {
    getReportSession(reportId: $reportId, sessionId: $sessionId) {
      sessionId userId managerId status readyAt completedAt cancelledAt
      sections layout agenda scheduledAt updatedAt taskSnapshot
    }
  }
`

const ON_SESSION_CHANGED = `
  subscription OnSessionChanged($sessionId: ID!) {
    onSessionChanged(sessionId: $sessionId) {
      sessionId userId managerId status readyAt completedAt cancelledAt
      sections layout agenda scheduledAt updatedAt taskSnapshot
    }
  }
`

const DEFAULT_LAYOUT = [...PREP_CORE_IDS, ...SESSION_CORE_IDS]


export function Canvas({ session, onUpdate, onSessionPatch, onReady, onCancel, onComplete, readOnly = false, viewingReportName = null, managerName = null, reportId = null, ownerUserId = null, onNavigateGoals = null, currentUserId = null }) {
  const [localSections, setLocalSections] = useState(session?.sections || {})
  const [localLayout, setLocalLayout] = useState(session?.layout || DEFAULT_LAYOUT)
  const [localAgenda, setLocalAgenda] = useState(session?.agenda || { items: [] })
  const [isDirty, setIsDirty] = useState(false)
  const dirtySectionsRef = useRef(new Set())
  const layoutDirtyRef = useRef(false)
  const lastUpdatedAtRef = useRef(session?.updatedAt || '')
  const [transitioning, setTransitioning] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [scheduledAt, setScheduledAt] = useState(session?.scheduledAt ? session.scheduledAt.slice(0, 16) : '')
  const [dragId, setDragId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)
  const [flippedCardId, setFlippedCardId] = useState(null)
  const [cardHistoryCache, setCardHistoryCache] = useState({})
  const cardHistory = useCardHistory(flippedCardId, ownerUserId)

  useEffect(() => {
    setLocalSections(session?.sections || {})
    setLocalLayout(session?.layout || DEFAULT_LAYOUT)
    setLocalAgenda(session?.agenda || { items: [] })
    setScheduledAt(session?.scheduledAt ? session.scheduledAt.slice(0, 16) : '')
    setIsDirty(false)
    dirtySectionsRef.current.clear()
    layoutDirtyRef.current = false
    lastUpdatedAtRef.current = session?.updatedAt || ''
  }, [session?.sessionId])

  // Sync from session prop when the parent's session state updates (e.g. via
  // onSessionListChanged subscription).  This is a fallback for when the direct
  // onSessionChanged subscription doesn't deliver.
  useEffect(() => {
    if (!session?.updatedAt) return
    if (session.updatedAt <= lastUpdatedAtRef.current) return
    lastUpdatedAtRef.current = session.updatedAt
    setScheduledAt(session.scheduledAt ? session.scheduledAt.slice(0, 16) : '')
    if (!isDirty) {
      const sections = typeof session.sections === 'string' ? JSON.parse(session.sections) : session.sections
      setLocalSections(sections || {})
      setLocalLayout(session.layout || DEFAULT_LAYOUT)
    }
  }, [session?.updatedAt, isDirty])

  // Apply real-time updates from the other party via subscription.
  // Agenda is managed by its own hook (useAgenda) and is safe to apply
  // even while prep cards have unsaved edits (isDirty).
  const handlePollResult = useCallback((updated) => {
    if (!updated) return
    if (updated.updatedAt <= lastUpdatedAtRef.current) return  // nothing new
    lastUpdatedAtRef.current = updated.updatedAt
    // Parse AWSJSON strings from the WebSocket subscription into objects
    const parsed = { ...updated }
    if (typeof parsed.sections === 'string') parsed.sections = JSON.parse(parsed.sections)
    if (typeof parsed.agenda === 'string') parsed.agenda = JSON.parse(parsed.agenda)
    if (typeof parsed.goalSnapshot === 'string') parsed.goalSnapshot = JSON.parse(parsed.goalSnapshot)
    if (typeof parsed.taskSnapshot === 'string') parsed.taskSnapshot = JSON.parse(parsed.taskSnapshot)
    setScheduledAt(parsed.scheduledAt ? parsed.scheduledAt.slice(0, 16) : '')
    if (!isDirty) {
      setLocalSections(parsed.sections || {})
      setLocalLayout(parsed.layout || DEFAULT_LAYOUT)
    }
    onUpdate?.(parsed.sessionId, parsed, { silent: true })
  }, [isDirty, onUpdate])

  const { connected: subscriptionConnected } = useSubscription(
    ON_SESSION_CHANGED,
    { sessionId: session?.sessionId },
    (data) => handlePollResult(data.onSessionChanged),
    { enabled: !!session?.sessionId && session?.status !== 'completed' && session?.status !== 'cancelled' },
  )

  // Poll for updates only when the subscription WebSocket is disconnected.
  // When the subscription is active it delivers changes in real-time, so
  // polling is unnecessary.
  useEffect(() => {
    if (subscriptionConnected) return
    if (!session?.sessionId || session?.status === 'completed' || session?.status === 'cancelled') return
    const poll = async () => {
      try {
        const data = reportId
          ? await appsyncQuery(GET_REPORT_SESSION, { reportId, sessionId: session.sessionId })
          : await appsyncQuery(GET_SESSION, { sessionId: session.sessionId })
        const result = data.getReportSession ?? data.getSession
        if (result) handlePollResult(result)
      } catch { /* ignore poll errors */ }
    }
    // Fetch once immediately when falling back to polling (subscription just dropped)
    poll()
    const id = setInterval(poll, 5000)
    return () => clearInterval(id)
  }, [subscriptionConnected, session?.sessionId, session?.status, reportId, handlePollResult])

  const handleSave = useCallback(async (id, data) => {
    const dirtyCards = [...dirtySectionsRef.current]
    const layoutWasDirty = layoutDirtyRef.current
    const patch = {}
    if (dirtyCards.length > 0) {
      patch.sections = data.sections
    }
    if (layoutWasDirty) {
      patch.layout = data.layout
    }
    if (Object.keys(patch).length === 0) {
      return
    }
    await onUpdate?.(id, patch)
    // Only clear entries that were dirty at save time, preserving any changes made during the await
    dirtyCards.forEach((k) => dirtySectionsRef.current.delete(k))
    if (layoutWasDirty) layoutDirtyRef.current = false
    if (dirtySectionsRef.current.size === 0 && !layoutDirtyRef.current) setIsDirty(false)
  }, [onUpdate])

  const isDraft = session?.status === 'draft'
  const isReady = session?.status === 'ready'

  const autoSaveData = useMemo(
    () => ({ sections: localSections, layout: localLayout }),
    [localSections, localLayout]
  )

  const saveState = useAutoSave(
    session?.sessionId,
    autoSaveData,
    handleSave,
    !readOnly && (isDraft || isReady)
  )

  function handleSectionChange(key, value) {
    setLocalSections((prev) => ({ ...prev, [key]: value }))
    dirtySectionsRef.current.add(key)
    setIsDirty(true)
  }

  function handleRemoveCard(id) {
    setLocalLayout((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((cid) => cid !== id)
    })
    layoutDirtyRef.current = true
    setIsDirty(true)
  }

  const handleCardFlip = useCallback((cardId) => {
    setFlippedCardId((prev) => {
      if (prev === cardId) return null
      return cardId
    })
  }, [])

  // Fetch history when a card is flipped open (cache directly from the result)
  useEffect(() => {
    if (flippedCardId && !cardHistoryCache[flippedCardId]) {
      cardHistory.fetch().then((result) => {
        if (result) {
          setCardHistoryCache((prev) => ({ ...prev, [flippedCardId]: result }))
        }
      })
    }
  }, [flippedCardId, cardHistory.fetch])

  function handleAddCard(id) {
    setLocalLayout((prev) => [...prev, id])
    layoutDirtyRef.current = true
    setIsDirty(true)
  }

  function handleDragStart(e, id) {
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e, id) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (id !== dragId) setDragOverId(id)
  }

  function handleDrop(e, id) {
    e.preventDefault()
    if (!dragId || dragId === id) return
    setLocalLayout((prev) => {
      const next = [...prev]
      const from = next.indexOf(dragId)
      const to = next.indexOf(id)
      if (from === -1 || to === -1) return prev
      next.splice(from, 1)
      next.splice(to, 0, dragId)
      return next
    })
    layoutDirtyRef.current = true
    setIsDirty(true)
    setDragId(null)
    setDragOverId(null)
  }

  function handleDragEnd() {
    setDragId(null)
    setDragOverId(null)
  }

  async function handleReady() {
    setTransitioning(true)
    try {
      await onReady?.(session.sessionId)
    } finally {
      setTransitioning(false)
    }
  }

  async function handleCancel() {
    setTransitioning(true)
    try {
      await onCancel?.(session.sessionId)
    } finally {
      setTransitioning(false)
    }
  }

  async function handleComplete() {
    setCompleting(true)
    try {
      await onComplete?.(session.sessionId)
    } finally {
      setCompleting(false)
    }
  }

  async function handleScheduledAtChange(e) {
    const value = e.target.value
    setScheduledAt(value)
    await onUpdate?.(session.sessionId, { scheduledAt: value ? new Date(value).toISOString() : null })
  }

  if (!session) return null
  const isFullyLocked = session.status === 'completed' || session.status === 'cancelled'
  const isLocked = readOnly || isFullyLocked
  const isManagerView = !!reportId
  // Prep is report-owned: only the report edits prep cards / tray / schedule / ready.
  const canEdit = isDraft && !readOnly && !isManagerView
  // Session-card tray and reorder is available to both sides during draft+ready.
  const canAddSessionCards = (isDraft || isReady) && !isFullyLocked && !readOnly
  // In-session content is filled during the live session (ready state).
  const canEditSession = isReady && !readOnly

  const prepLayout = localLayout.filter((id) => PREP_CARD_IDS.has(id))
  const sessionLayout = localLayout.filter((id) => SESSION_CARD_IDS.has(id))

  return (
    <div style={styles.canvas} data-testid="canvas">
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.titleRow}>
            <h2 style={styles.title}>1:1 Prep</h2>
            {(() => {
              const tone = session.status === 'completed' ? 'success'
                : session.status === 'ready' ? 'info'
                : session.status === 'cancelled' ? 'danger'
                : 'neutral'
              const label = session.status === 'completed' ? 'Completed ✓'
                : session.status === 'ready' ? 'Ready ✓'
                : session.status === 'cancelled' ? 'Cancelled'
                : 'Draft'
              return <Pill tone={tone} style={{ fontSize: 11, padding: '3px 8px' }}>{label}</Pill>
            })()}
          </div>
          {!isFullyLocked && (
            <div style={styles.scheduledLabel}>
              <TextField
                type="datetime-local"
                label="Scheduled for"
                step="900"
                value={scheduledAt}
                onChange={handleScheduledAtChange}
                disabled={readOnly || isReady || isManagerView}
              />
            </div>
          )}
        </div>
        <div style={styles.headerRight}>
          {canEditSession && (
            <ConfirmFlipButton
              label="Cancel 1:1"
              warning="Mark this 1:1 as cancelled? The session will be fully locked."
              confirmLabel="Yes, cancel 1:1"
              confirmingLabel="Cancelling..."
              onConfirm={handleCancel}
              transitioning={transitioning}
              variant="danger"
            />
          )}
        </div>
      </div>

      {/* Lock banner (not shown for viewingReport — that notice is inline on the section divider) */}
      {(isFullyLocked || isReady) && (
        <SessionLockBanner
          status={session.status}
          readyAt={session.readyAt}
          completedAt={session.completedAt}
          cancelledAt={session.cancelledAt}
        />
      )}

      {/* ── Section 1: Goals ─────────────────────────────────── */}
      {ownerUserId && (
        <>
          <div style={styles.sectionDivider}>
            <button style={styles.sectionDividerLink} onClick={() => onNavigateGoals?.()}>
              Goals ↗
            </button>
          </div>
          <GoalCanvasZone
            ownerUserId={ownerUserId}
            sessionId={session?.sessionId}
            goalSnapshot={session?.goalSnapshot ?? null}
            onNavigateGoals={onNavigateGoals}
            readOnly={isFullyLocked}
          />
        </>
      )}

      {/* ── Section 2: Agenda ────────────────────────────────── */}
      <div style={styles.sectionDivider}>
        <span style={styles.sectionDividerLabel}>Agenda</span>
      </div>
      <AgendaSection
        sessionId={session.sessionId}
        reportId={reportId}
        agenda={localAgenda}
        locked={isFullyLocked}
        currentUserId={currentUserId}
        otherUserName={viewingReportName || managerName}
        onSaved={(updated) => {
          const agenda = typeof updated.agenda === 'string' ? JSON.parse(updated.agenda) : updated.agenda
          setLocalAgenda(agenda || { items: [] })
          if (updated.updatedAt) lastUpdatedAtRef.current = updated.updatedAt
          onSessionPatch?.(session.sessionId, { agenda, updatedAt: updated.updatedAt })
        }}
      />

      {/* ── Section 3: Session Preparation ───────────────────── */}
      {(prepLayout.length > 0 || canEdit) && (
        <>
          <div style={styles.sectionDivider}>
            <span style={styles.sectionDividerLabel}>Session preparation</span>
            {viewingReportName && (
              <span style={styles.viewingNotice}>
                👁 Viewing {viewingReportName}'s prep · read-only
              </span>
            )}
            {canEdit && (
              <div style={styles.sectionAction}>
                <ReadyFlipButton scheduledAt={scheduledAt} onReady={handleReady} transitioning={transitioning} cancelFirst />
              </div>
            )}
          </div>
          {prepLayout.length > 0 && (
            <div style={styles.grid}>
              {prepLayout.map((id) => {
                const cardDef = getPrepCard(id)
                if (!cardDef) return null
                const isDragging = dragId === id
                const isDragOver = dragOverId === id
                return (
                  <div
                    key={id}
                    draggable={canEdit}
                    onDragStart={canEdit ? (e) => handleDragStart(e, id) : undefined}
                    onDragOver={canEdit ? (e) => handleDragOver(e, id) : undefined}
                    onDrop={canEdit ? (e) => handleDrop(e, id) : undefined}
                    onDragEnd={canEdit ? handleDragEnd : undefined}
                    style={{
                      opacity: isDragging ? 0.4 : 1,
                      outline: isDragOver ? '2px dashed var(--text-secondary)' : 'none',
                      borderRadius: '8px',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {CARD_HISTORY_CONFIGS[id] ? (() => {
                      const isFlipped = flippedCardId === id
                      const cached = cardHistoryCache[id]
                      const historyData = isFlipped ? (cached || cardHistory.data) : cached
                      return (
                        <div style={flipStyles.container}>
                          <div style={{ ...flipStyles.inner, ...(isFlipped ? flipStyles.rotated : {}) }}>
                            <div style={flipStyles.front}>
                              <PrepCard
                                cardDef={cardDef}
                                data={localSections[id]}
                                onChange={(val) => handleSectionChange(id, val)}
                                disabled={isLocked || isReady || isManagerView}
                                onRemove={canEdit ? handleRemoveCard : undefined}
                                ownerUserId={ownerUserId}
                                onHeaderClick={() => handleCardFlip(id)}
                              />
                            </div>
                            <div style={flipStyles.back}>
                              <CardHistoryBack
                                cardDef={cardDef}
                                entries={historyData?.entries ?? null}
                                loading={isFlipped && cardHistory.loading}
                                error={isFlipped ? cardHistory.error : null}
                                onFlip={() => handleCardFlip(id)}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })() : (
                      <PrepCard
                        cardDef={cardDef}
                        data={localSections[id]}
                        onChange={(val) => handleSectionChange(id, val)}
                        disabled={isLocked || isReady || isManagerView}
                        onRemove={canEdit ? handleRemoveCard : undefined}
                        ownerUserId={ownerUserId}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {canEdit && (
            <PrepCardTray layout={localLayout} onAddCard={handleAddCard} />
          )}
        </>
      )}

      {/* ── Section 4: In Session ─────────────────────────────── */}
      {(sessionLayout.length > 0 || canAddSessionCards) && (
        <>
          <div style={styles.sectionDivider}>
            <span style={styles.sectionDividerLabel}>In session</span>
            {canEditSession && (
              <div style={styles.sectionAction}>
                <ConfirmFlipButton
                  label="Complete session"
                  warning="Once completed, this session can no longer be edited."
                  confirmLabel="Yes, complete"
                  confirmingLabel="Completing..."
                  onConfirm={handleComplete}
                  transitioning={completing}
                  cancelFirst
                />
              </div>
            )}
          </div>
          {sessionLayout.length > 0 && (
            <div style={styles.grid}>
              {sessionLayout.map((id) => {
                const cardDef = getSessionCard(id)
                if (!cardDef) return null
                const isDragging = dragId === id
                const isDragOver = dragOverId === id
                // Pull from prep: supply callback for talking-points card
                const prepTopicsData = cardDef.config?.pullFromPrepCard
                  ? localSections[cardDef.config.pullFromPrepCard]
                  : null
                const hasPrepItems = prepTopicsData?.items?.length > 0
                const alreadyPulled = localSections[id]?.pulledFromPrep
                const onPullFromPrep = (hasPrepItems && !alreadyPulled)
                  ? () => {
                      const srcItems = prepTopicsData.items
                      const existing = localSections[id]?.items ?? []
                      const pulled = srcItems.map((text) => ({ text, discussed: false }))
                      handleSectionChange(id, {
                        ...(localSections[id] ?? {}),
                        items: [...existing, ...pulled],
                        pulledFromPrep: true,
                      })
                    }
                  : undefined
                return (
                  <div
                    key={id}
                    draggable={canAddSessionCards}
                    onDragStart={canAddSessionCards ? (e) => handleDragStart(e, id) : undefined}
                    onDragOver={canAddSessionCards ? (e) => handleDragOver(e, id) : undefined}
                    onDrop={canAddSessionCards ? (e) => handleDrop(e, id) : undefined}
                    onDragEnd={canAddSessionCards ? handleDragEnd : undefined}
                    style={{
                      opacity: isDragging ? 0.4 : 1,
                      outline: isDragOver ? '2px dashed var(--text-secondary)' : 'none',
                      borderRadius: '8px',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <SessionCard
                      cardDef={cardDef}
                      data={localSections[id]}
                      onChange={(val) => handleSectionChange(id, val)}
                      readOnly={isLocked || isDraft}
                      onRemove={canAddSessionCards ? handleRemoveCard : undefined}
                      onPullFromPrep={onPullFromPrep}
                      isManagerView={!!reportId}
                    />
                  </div>
                )
              })}
            </div>
          )}
          {canAddSessionCards && (
            <SessionCardTray layout={localLayout} onAddCard={handleAddCard} />
          )}
        </>
      )}

      {/* ── Section 5: Tasks ──────────────────────────────────── */}
      {session.userId && session.managerId && currentUserId && (
        <>
          <div style={styles.sectionDivider}>
            <span style={styles.sectionDividerLabel}>Tasks</span>
          </div>
          <TaskList
            mode={isFullyLocked ? 'snapshot' : 'live'}
            reportId={session.userId}
            managerId={session.managerId}
            currentUserId={currentUserId}
            reportName={viewingReportName || 'You'}
            managerName={managerName || 'Your manager'}
            snapshotTasks={isFullyLocked ? (session.taskSnapshot?.tasks ?? []) : undefined}
            sortPersistenceKey="canvas"
            softCloseFilter={!isFullyLocked}
          />
        </>
      )}

      {/* ── Section 6: Private Notes ──────────────────────────── */}
      {(reportId || (!readOnly && (!isFullyLocked || session.privateNotes))) && (
        <>
          <div style={styles.sectionDivider}>
            <span style={styles.sectionDividerLabel}>Private notes</span>
          </div>
          <PrivateNotesCard
            sessionId={session.sessionId}
            reportId={reportId}
            initialNotes={reportId ? undefined : (session.privateNotes || '')}
            readOnly={!reportId && isFullyLocked}
          />
        </>
      )}

      <PrintView
        session={session}
        layout={localLayout}
        sections={localSections}
      />

      {isDirty && saveState === 'idle' && (
        <div style={styles.saveStatus}>Unsaved changes</div>
      )}
      {(saveState === 'saving' || saveState === 'saved') && (
        <div style={styles.saveStatus}>
          {saveState === 'saving' ? 'Saving...' : 'Saved'}
        </div>
      )}
      {saveState === 'error' && (
        <div style={{ ...styles.saveStatus, color: '#c45a5a' }}>Save failed — changes not saved</div>
      )}
    </div>
  )
}

const flipStyles = {
  container: {
    perspective: '800px',
  },
  inner: {
    transition: 'transform 0.6s ease',
    transformStyle: 'preserve-3d',
    position: 'relative',
  },
  rotated: {
    transform: 'rotateY(180deg)',
  },
  front: {
    backfaceVisibility: 'hidden',
  },
  back: {
    backfaceVisibility: 'hidden',
    position: 'absolute',
    inset: 0,
    transform: 'rotateY(180deg)',
  },
}

const styles = {
  canvas: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  titleRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em' },
  scheduledLabel: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' },
  headerRight: { display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
  sectionAction: { marginLeft: 'auto' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '16px',
  },
  sectionDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '4px',
  },
  sectionDividerLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
  },
  viewingNotice: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
  sectionDividerLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
    padding: 0,
  },
  saveStatus: {
    position: 'sticky',
    bottom: '20px',
    alignSelf: 'center',
    fontSize: '11px',
    color: 'var(--text-secondary)',
    pointerEvents: 'none',
  },
}
